<script setup lang="ts">
import { router, usePage, Link } from "@inertiajs/vue3"
import { computed, onMounted, ref, watch, type Ref } from "vue"
import route from "ziggy-js"
import {
    BookOpen,
    BookMarked,
    CalendarDays,
    ChevronLeft,
    ChevronRight,
    ClipboardList,
    Clock,
    Coffee,
    GraduationCap,
    LayoutDashboard,
    LogOut,
    Users,
    Wallet,
    X,
} from "lucide-vue-next"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

const STORAGE_KEY = "antahan.sidebar.collapsed"

const props = defineProps<{
    collapsed: boolean
    mobileOpen: boolean
}>()

const emit = defineEmits<{
    "update:collapsed": [value: boolean]
    "update:mobileOpen": [value: boolean]
}>()

const page = usePage()

const user = computed(() => page.props.user) as Readonly<Ref<{ [key: string]: any }>>
const links = computed(() => page.props.inertiaLinks) as Readonly<Ref<{ [key: string]: any }>>
const features = computed(() => (page.props.features as Record<string, boolean>) ?? {})
const isAdmin = computed(() => Boolean(user.value?.is_admin))

onMounted(() => {
    const stored = localStorage.getItem(STORAGE_KEY) === "1"
    if (stored !== props.collapsed) {
        emit("update:collapsed", stored)
    }
})

watch(
    () => props.collapsed,
    (value) => {
        localStorage.setItem(STORAGE_KEY, value ? "1" : "0")
    }
)

function getPath(url: string) {
    return url.replace(/^.*\/\/[^\/]+/, "")
}

function isActive(path: string, match: "exact" | "prefix" = "exact") {
    const current = page.url.split("?")[0]
    const target = getPath(path)

    if (match === "prefix") {
        return current === target || current.startsWith(`${target}/`)
    }

    return current === target
}

const navItems = computed(() => {
    const items = [
        {
            label: "Dashboard",
            href: "/",
            icon: LayoutDashboard,
            adminOnly: false,
            feature: null,
            match: "exact" as const,
        },
        {
            label: "Time Clock",
            href: links.value.time_clock_path,
            icon: Clock,
            adminOnly: false,
            feature: null,
            match: "prefix" as const,
        },
        {
            label: "Attendance",
            href: links.value.attendances_path,
            icon: ClipboardList,
            adminOnly: true,
            feature: null,
            match: "prefix" as const,
        },
        {
            label: "Finance",
            href: links.value.finance_path,
            icon: Wallet,
            adminOnly: true,
            feature: "dashboard",
            match: "exact" as const,
        },
        {
            label: "Transactions",
            href: links.value.transactions_path,
            icon: BookOpen,
            adminOnly: true,
            feature: "transactions",
            match: "exact" as const,
        },
        {
            label: "Schedule",
            href: links.value.shifts_path,
            icon: CalendarDays,
            adminOnly: true,
            feature: "shifts",
            match: "exact" as const,
        },
        {
            label: "Employees",
            href: links.value.employees_path,
            icon: Users,
            adminOnly: true,
            feature: null,
            match: "prefix" as const,
        },
        {
            label: "Recipes",
            href: links.value.recipes_path,
            icon: Coffee,
            adminOnly: false,
            feature: null,
            match: "prefix" as const,
        },
        {
            label: "Barista Manual",
            href: links.value.barista_manual_path,
            icon: BookMarked,
            adminOnly: false,
            feature: null,
            match: "prefix" as const,
        },
        {
            label: "Barista Onboarding",
            href: links.value.barista_onboarding_path,
            icon: GraduationCap,
            adminOnly: true,
            feature: "barista_onboarding",
            match: "prefix" as const,
        },
    ]

    return items.filter((item) => {
        if (item.adminOnly && !isAdmin.value) {
            return false
        }

        if (item.feature && !features.value[item.feature]) {
            return false
        }

        return true
    })
})

function toggleCollapsed() {
    emit("update:collapsed", !props.collapsed)
}

function closeMobile() {
    emit("update:mobileOpen", false)
}

function logout() {
    router.post(route("logout"))
}
</script>

<template>
    <aside
        :class="
            cn(
                'print-hide fixed inset-y-0 left-0 z-50 flex h-full flex-col border-r border-gray-200 bg-white transition-[width,transform] duration-200 ease-in-out lg:static lg:translate-x-0',
                collapsed ? 'lg:w-[4.5rem]' : 'lg:w-64',
                mobileOpen ? 'w-72 translate-x-0' : 'w-72 -translate-x-full lg:translate-x-0'
            )
        "
    >
        <Teleport to="body">
            <div
                v-if="mobileOpen"
                class="print-hide fixed inset-0 z-40 bg-black/40 lg:hidden"
                @click="closeMobile"
            />
        </Teleport>

        <div class="flex h-14 items-center justify-between gap-2 border-b px-3 lg:h-16">
            <Link href="/" class="flex min-w-0 items-center gap-2 overflow-hidden" @click="closeMobile">
                <img src="/images/antahan-logo.svg" alt="Antahan Café" class="h-8 w-8 shrink-0 object-contain" />
                <span class="truncate text-sm font-semibold text-gray-900" :class="{ 'lg:hidden': collapsed }">
                    Antahan Café
                </span>
            </Link>

            <Button type="button" variant="ghost" size="icon" class="lg:hidden" @click="closeMobile">
                <X class="h-5 w-5" />
            </Button>

            <Button
                type="button"
                variant="ghost"
                size="icon"
                class="hidden shrink-0 lg:inline-flex"
                :title="collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
                @click="toggleCollapsed"
            >
                <ChevronRight v-if="collapsed" class="h-4 w-4" />
                <ChevronLeft v-else class="h-4 w-4" />
            </Button>
        </div>

        <nav class="flex-1 space-y-1 overflow-y-auto p-3">
            <Link
                v-for="item in navItems"
                :key="item.label"
                :href="item.href"
                :title="item.label"
                :class="
                    cn(
                        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        isActive(item.href, item.match)
                            ? 'bg-[rgb(181_115_5)]/10 text-[rgb(145_90_0)]'
                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
                        collapsed && 'lg:justify-center lg:px-2'
                    )
                "
                @click="closeMobile"
            >
                <component :is="item.icon" class="h-5 w-5 shrink-0" />
                <span :class="cn('truncate', collapsed && 'lg:hidden')">{{ item.label }}</span>
            </Link>
        </nav>

        <div class="border-t p-3">
            <div
                :class="
                    cn('mb-2 flex items-center gap-3 rounded-md px-3 py-2', collapsed && 'lg:justify-center lg:px-2')
                "
            >
                <div
                    class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[rgb(181_115_5)]/15 text-xs font-semibold text-[rgb(145_90_0)]"
                >
                    {{ user?.name?.charAt(0)?.toUpperCase() ?? "?" }}
                </div>
                <div :class="cn('min-w-0', collapsed && 'lg:hidden')">
                    <p class="truncate text-sm font-medium text-gray-900">{{ user?.name }}</p>
                    <p class="truncate text-xs text-gray-500">{{ user?.email }}</p>
                </div>
            </div>

            <button
                type="button"
                :class="
                    cn(
                        'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900',
                        collapsed && 'lg:justify-center lg:px-2'
                    )
                "
                title="Log out"
                @click="logout"
            >
                <LogOut class="h-5 w-5 shrink-0" />
                <span :class="cn(collapsed && 'lg:hidden')">Log out</span>
            </button>
        </div>
    </aside>
</template>
